import { Link } from "react-router-dom";
import { MainLayout } from "@/components/layout/MainLayout";
import { PageMeta } from "@/components/PageMeta";
import coloringShop from "@/assets/coloring-shop.png";
// Tier logos
import tierColoringColony from "@/assets/tier-coloring-with-colony.png";
import tierFluffColor from "@/assets/tier-fluff-and-color.png";
import tierFluffFoundation from "@/assets/tier-fluff-foundation.png";
import tierCultFluff from "@/assets/tier-cult-of-fluff.webp";
import customOrdersFabrics from "@/assets/custom-orders-fabrics.png";
import premadeProducts from "@/assets/premade-products.png";

const tierLogos = [tierColoringColony, tierFluffColor, tierFluffFoundation, tierCultFluff];

const Home = () => {
  const navItems = [
    {
      title: "Memberships",
      description: "Digital content & handmade sewn products for sugar glider lovers",
      icon: null,
      href: "/memberships",
      gradient: "from-brand-purple/30 to-brand-magenta/30",
      border: "border-brand-purple/40",
    },
    {
      title: "Custom Orders",
      description: "Build your set with the pieces you want in the fabric of your choice",
      icon: null,
      image: customOrdersFabrics,
      href: "/custom-orders",
      gradient: "from-brand-teal/30 to-brand-green/30",
      border: "border-brand-teal/40",
    },
    {
      title: "Premade Shop",
      description: "Ready to ship Go Fluff items",
      icon: null,
      image: premadeProducts,
      href: "/premades",
      gradient: "from-brand-pink/30 to-brand-orange/30",
      border: "border-brand-pink/40",
    },
    {
      title: "Coloring Shop",
      description: "Instant download sugar glider coloring pages — adult & kids printable art",
      icon: null,
      image: coloringShop,
      href: "/coloring-shop",
      gradient: "from-[hsl(230,60%,30%)]/30 to-[hsl(270,50%,40%)]/30",
      border: "border-[hsl(250,50%,50%)]/40",
    },
  ];

  // Organization schema
  const orgJsonLd = {
    "@context": "https://schema.org",
    "@type": "Organization",
    "name": "Go Fluff and Stuff",
    "url": "https://gofluffandstuff.com",
    "logo": "https://gofluffandstuff.com/logo.png",
    "description": "Handmade sugar glider cage accessories, fleece bedding sets, bonding pouches, hammocks, custom fabric orders, digital memberships, and printable coloring pages.",
    "sameAs": [
      "https://www.facebook.com/gofluffandstuff",
      "https://www.instagram.com/gofluffandstuff"
    ],
    "offers": [
      { "@type": "Offer", "name": "Custom Orders", "url": "https://gofluffandstuff.com/custom-orders" },
      { "@type": "Offer", "name": "Premade Shop", "url": "https://gofluffandstuff.com/premades" },
      { "@type": "Offer", "name": "Coloring Shop", "url": "https://gofluffandstuff.com/coloring-shop" },
      { "@type": "Offer", "name": "Memberships", "url": "https://gofluffandstuff.com/memberships" }
    ]
  };

  // FAQ schema — mirrors FAQSection.tsx questions
  const faqJsonLd = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": [
      {
        "@type": "Question",
        "name": "How do the monthly subscriptions work?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "When you subscribe to a tier, you're billed monthly via PayPal. Digital content (wallpapers, coloring pages) is available immediately in your account. Physical items are handmade and shipped once per month, typically within the first two weeks."
        }
      },
      {
        "@type": "Question",
        "name": "Can I change my subscription tier?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Yes! You can upgrade or downgrade your tier at any time. Changes take effect at your next billing cycle. Each tier is independent, so switching tiers gives you access to that specific tier's benefits only."
        }
      },
      {
        "@type": "Question",
        "name": "Do higher tiers include lower tier benefits?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "No, each subscription tier is separate and includes only its own benefits. If you want both digital content and physical items, you'd need to consider which tier best fits your needs or subscribe to multiple tiers."
        }
      },
      {
        "@type": "Question",
        "name": "What's included in the physical subscription boxes?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Fluff Foundation members receive 2 handmade cage pieces monthly in subtle tone-on-tone prints. Cult of Fluff members get 2 pieces from exclusive custom fabric sets, plus surprise extras and early access to new designs."
        }
      },
      {
        "@type": "Question",
        "name": "How do I access my digital downloads?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Once subscribed, log into your account and visit the 'Digital Downloads' section. All wallpapers and coloring pages you have access to will be available for instant download."
        }
      },
      {
        "@type": "Question",
        "name": "Can I cancel my subscription?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Absolutely! You can cancel anytime through PayPal's subscription management. You'll continue to have access until the end of your current billing period."
        }
      },
      {
        "@type": "Question",
        "name": "Do you ship internationally?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "Currently, physical subscription boxes ship within the United States. Digital-only tiers (Coloring with the Colony, Fluff and Color) are available worldwide!"
        }
      },
      {
        "@type": "Question",
        "name": "What if I have a problem with my order?",
        "acceptedAnswer": {
          "@type": "Answer",
          "text": "We're here to help! Reach out to us and we'll make it right. Your happiness with your fluffy goodies is our top priority."
        }
      }
    ]
  };

  return (
    <MainLayout>
      <PageMeta
        title="Go Fluff and Stuff — Handmade Sugar Glider Cage Accessories"
        description="Handmade sugar glider cage sets, fleece pouches, hammocks & bedding. Monthly membership boxes, custom fabric orders, and instant-download coloring pages. Safety-first, AVL-listed materials."
        path="/"
      />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(orgJsonLd) }} />
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(faqJsonLd) }} />
      <section className="relative py-8 md:py-12 overflow-hidden min-h-screen">
        {/* Animated gradient background */}
        <div className="absolute inset-0 bg-gradient-dark" />
        <div className="absolute inset-0 bg-gradient-glow" />
        
        {/* Floating orbs for visual interest */}
        <div className="absolute top-20 left-10 w-64 h-64 bg-brand-purple/10 rounded-full blur-3xl animate-pulse" />
        <div className="absolute bottom-20 right-10 w-48 h-48 bg-brand-magenta/10 rounded-full blur-3xl animate-pulse" style={{ animationDelay: '1s' }} />
        <div className="absolute top-1/2 left-1/3 w-32 h-32 bg-brand-pink/10 rounded-full blur-2xl animate-pulse" style={{ animationDelay: '0.5s' }} />
        
        <div className="container relative z-10 flex flex-col items-center text-center px-4">
          {/* Logo */}
          <div className="relative">
            <div className="absolute inset-0 bg-brand-purple/30 rounded-full blur-3xl scale-150" />
            <img 
              src="/logo.png" 
              alt="Go Fluff and Stuff - Sugar Glider Logo"
              className="relative w-32 md:w-44 h-auto mb-6 animate-float drop-shadow-2xl"
              width={176}
              height={176}
              fetchPriority="high"
            />
          </div>
          
          {/* Title */}
          <h1 className="text-3xl md:text-5xl font-bold text-foreground mb-4">
            Welcome to{" "}
            <span className="text-rainbow-gradient font-display">Go Fluff and Stuff</span>
          </h1>
          
          <p className="text-base md:text-lg text-muted-foreground max-w-xl mb-10 leading-relaxed">
            Handmade sugar glider accessories, digital memberships, and custom fabric orders.
          </p>
          
          {/* Navigation Cards */}
          <div className="grid grid-cols-2 gap-3 sm:gap-4 w-full max-w-3xl mb-8 min-h-[350px]">
            {navItems.map((item) => (
              <Link
                key={item.title}
                to={item.href}
                className={`
                  group relative rounded-2xl overflow-hidden
                  bg-gradient-to-br ${item.gradient}
                  border ${item.border}
                  backdrop-blur-sm
                  transition-all duration-300
                  hover:scale-[1.02] hover:shadow-lg hover:shadow-brand-purple/20
                  active:scale-[0.98]
                  p-4 sm:p-6 text-left
                `}
              >
                <div className="flex flex-col items-center text-center gap-2 sm:flex-row sm:items-start sm:text-left sm:gap-4">
                  {item.icon ? (
                    <div className="p-3 rounded-xl bg-background/50 backdrop-blur-sm">
                      <item.icon className="h-6 w-6 text-foreground" />
                    </div>
                  ) : item.image ? (
                    <div className="w-12 h-12 rounded-xl bg-background/50 backdrop-blur-sm overflow-hidden flex-shrink-0">
                      <img 
                        src={item.image} 
                        alt="" 
                        className="w-full h-full object-cover"
                        width={48}
                        height={48}
                        loading="lazy"
                      />
                    </div>
                  ) : (
                    <div className="grid grid-cols-2 gap-1 p-1.5 rounded-xl bg-background/50 backdrop-blur-sm flex-shrink-0">
                      {tierLogos.map((tierLogo, idx) => (
                        <img 
                          key={idx} 
                          src={tierLogo} 
                          alt="" 
                          className="w-6 h-6 rounded-full object-cover aspect-square"
                          width={24}
                          height={24}
                          loading="lazy"
                        />
                      ))}
                    </div>
                  )}
                  <div className="min-w-0">
                    <h2 className="text-sm sm:text-lg font-semibold text-foreground mb-1 leading-tight">
                      {item.title}
                    </h2>
                    <p className="text-xs sm:text-sm text-muted-foreground leading-snug">
                      {item.description}
                    </p>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </MainLayout>
  );
};

export default Home;
